import React from 'react'
import { ClientSummary, EmailInUseError, ValidationError, api } from '../api/client'
import { NewClientDraft, emptyClientDraft, newClientErrors, toClientInput } from '../clients/newClient'
import Field, { useFocusFirstInvalid } from '../screens/settings/Field'
import { saveErrorMessage } from '../screens/settings/toast'

interface NewClientFormProps {
  // Prefilled first name (what she typed in a search).
  firstName?: string
  onCreated: (client: ClientSummary) => void
  onCancel: () => void
  cancelLabel: string
  onBusyChange?: (busy: boolean) => void
}

// NewClientForm adds one client: a real form (Enter adds), every problem
// shown at once, "Add client" the main action. Used by the Clients tab and
// by New booking.
export default function NewClientForm({ firstName = '', onCreated, onCancel, cancelLabel, onBusyChange }: NewClientFormProps) {
  const [draft, setDraft] = React.useState<NewClientDraft>({ ...emptyClientDraft, first_name: firstName })
  const [errors, setErrors] = React.useState<Record<string, string>>({})
  const [error, setError] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  const formRef = React.useRef<HTMLFormElement>(null)
  useFocusFirstInvalid(errors, formRef)

  const setBusy = (busy: boolean) => {
    setSaving(busy)
    onBusyChange?.(busy)
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    const found = newClientErrors(draft)
    if (Object.keys(found).length > 0) {
      setErrors(found)
      return
    }
    setBusy(true)
    setErrors({})
    try {
      const created = await api.createClient(toClientInput(draft))
      setBusy(false)
      onCreated(created)
    } catch (err) {
      if (err instanceof ValidationError) {
        setErrors(err.fields)
      } else if (err instanceof EmailInUseError) {
        setErrors({ email: saveErrorMessage(err) })
      } else {
        setError(saveErrorMessage(err))
      }
      setBusy(false)
    }
  }

  return (
    <form ref={formRef} className="settings-form sheet-form" onSubmit={submit} noValidate>
      <Field id="client-first-name" label="First name" error={errors.first_name}>
        <input
          id="client-first-name"
          autoComplete="off"
          value={draft.first_name}
          onChange={(e) => setDraft({ ...draft, first_name: e.target.value })}
        />
      </Field>
      <div className="field-pair">
        <Field id="client-last-name" label="Last name" error={errors.last_name}>
          <input
            id="client-last-name"
            autoComplete="off"
            value={draft.last_name}
            onChange={(e) => setDraft({ ...draft, last_name: e.target.value })}
          />
        </Field>
        <Field id="client-phone" label="Phone" error={errors.phone}>
          <input
            id="client-phone"
            type="tel"
            autoComplete="off"
            value={draft.phone}
            onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
          />
        </Field>
      </div>
      <Field id="client-email" label="Email" hint="For confirmations and reminders." error={errors.email}>
        <input
          id="client-email"
          type="email"
          inputMode="email"
          autoComplete="off"
          value={draft.email}
          onChange={(e) => setDraft({ ...draft, email: e.target.value })}
        />
      </Field>
      {error && <p className="field-error" role="alert">{error}</p>}
      <div className="booking-actions">
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Adding…' : 'Add client'}
        </button>
        <button type="button" className="btn btn-quiet btn-compact" onClick={onCancel} disabled={saving}>
          {cancelLabel}
        </button>
      </div>
    </form>
  )
}
